if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.submitButtonId = this.dataset.submitButtonId;
        this.stickyButton = this.querySelector('.sticky-atc__button');
        this.stickyButtonText = this.querySelector('.sticky-atc__button-text');
        this.stickyPrice = this.querySelector(`#StickyPrice-${this.sectionId}`);
        this.stickyMedia = this.querySelector(`#StickyMedia-${this.sectionId}`);
        this.isVisible = false;

        this.onStickyClick = this.onStickyClick.bind(this);
        this.syncFromMainButton = this.syncFromMainButton.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.stickyButton?.addEventListener('click', this.onStickyClick);
        this.bindObservers();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }
      }

      disconnectedCallback() {
        this.stickyButton?.removeEventListener('click', this.onStickyClick);
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.unsubscribeVariantChange?.();
        document.body.classList.remove('sticky-atc-visible');
      }

      get mainSubmitButton() {
        return document.getElementById(this.submitButtonId);
      }

      bindObservers() {
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();

        const mainButton = this.mainSubmitButton;
        if (!mainButton) return;

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(
            ([entry]) => {
              // Show only after the main ATC has scrolled above the viewport
              if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
                this.show();
              } else {
                this.hide();
              }
            },
            { threshold: 0 }
          );
          this.intersectionObserver.observe(mainButton);
        }

        this.mutationObserver = new MutationObserver(() => {
          this.syncFromMainButton();
        });

        this.mutationObserver.observe(mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.syncFromMainButton();
      }

      syncFromMainButton() {
        const mainButton = this.mainSubmitButton;
        if (!mainButton || !this.stickyButton) return;

        const isDisabled =
          mainButton.hasAttribute('disabled') || mainButton.getAttribute('aria-disabled') === 'true';

        this.stickyButton.toggleAttribute('disabled', isDisabled);
        this.stickyButton.classList.toggle('loading', mainButton.classList.contains('loading'));

        const mainText = mainButton.querySelector('span:not(.sold-out-message)');
        if (mainText && this.stickyButtonText) {
          this.stickyButtonText.innerHTML = mainText.innerHTML;
        }

        const spinner = this.stickyButton.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.toggle('hidden', !mainButton.classList.contains('loading'));
        }
      }

      onVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return;

        const html = event.data.html;
        if (!html) return;

        const sourcePrice =
          html.getElementById(`price-${this.sectionId}`) ||
          html.getElementById(`price-mobile-${this.sectionId}`) ||
          html.querySelector('.product-price');
        if (sourcePrice && this.stickyPrice) {
          this.stickyPrice.innerHTML = sourcePrice.innerHTML;
        }

        const sourceMedia = html.getElementById(`StickyMedia-${this.sectionId}`);
        if (sourceMedia && this.stickyMedia) {
          this.stickyMedia.innerHTML = sourceMedia.innerHTML;
        } else if (event.data.variant?.featured_media && this.stickyMedia) {
          const mediaId = `${this.sectionId}-${event.data.variant.featured_media.id}`;
          const galleryImage = document.querySelector(
            `media-gallery [data-media-id="${mediaId}"] img, .product__media-item[data-media-id="${mediaId}"] img`
          );
          const stickyImage = this.stickyMedia.querySelector('img');
          if (galleryImage && stickyImage) {
            stickyImage.src = galleryImage.currentSrc || galleryImage.src;
            if (galleryImage.srcset) stickyImage.srcset = galleryImage.srcset;
            stickyImage.alt = galleryImage.alt || '';
          }
        }

        requestAnimationFrame(() => this.bindObservers());
      }

      onStickyClick(event) {
        event.preventDefault();
        if (this.stickyButton.hasAttribute('disabled')) return;

        const mainButton = this.mainSubmitButton;
        if (!mainButton || mainButton.hasAttribute('disabled')) return;

        const ringSizeInput = document.querySelector('[name="properties[Ring Size]"]');
        const ringSizeError = document.querySelector('.ring-size--error');

        // No ring size selected — scroll to selector so the customer can choose one
        if (ringSizeInput && !ringSizeInput.value.trim()) {
          ringSizeError?.classList.remove('hidden');
          this.hide();
          const ringSizeField = ringSizeInput.closest('.sizes-meta-ring') || ringSizeInput;
          ringSizeField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          requestAnimationFrame(() => ringSizeInput.focus({ preventScroll: true }));
          return;
        }

        mainButton.click();
      }

      show() {
        if (this.isVisible) return;
        this.isVisible = true;
        this.classList.add('sticky-atc--visible');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('sticky-atc-visible');
      }

      hide() {
        if (!this.isVisible) return;
        this.isVisible = false;
        this.classList.remove('sticky-atc--visible');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sticky-atc-visible');
      }
    }
  );
}
